import * as THREE from "three";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { facets } from "./geom";

// The BMX (#141): a 20" freestyle bike in the yaw group's space (front wheel
// toward +z, ground at y 0). It's two halves that turn about the head tube,
// so tricks can spin them apart: `frame` (the frame, seat, cranks, back
// wheel and back pegs; a tailwhip spins it round) and `front` (fork, front
// wheel, front pegs, stem and bars; a barspin spins it). Both halves have
// their origin on the steering axis. Built only once someone gets on a bike.

/** Wheel radius (m): a 20" BMX wheel and tyre. */
export const BIKE_WHEEL_R = 0.255;
/** Where the hubs sit along the bike (z) from the steering axis: the front one is on it. */
export const BIKE_REAR_HUB = -0.92;
/** The steering axis's place along the bike, from the bike's middle (z). */
export const BIKE_HEAD_Z = 0.46;
/** Pegs stick out this far each side of the hubs (m, x), centre of the peg. */
export const BIKE_PEG_X = 0.13;
/** The pedals, cranks level, in the bike's space: the rider's feet. */
export const BIKE_PEDAL = new THREE.Vector3(0, 0.29, -0.12);
/** The pedals sit this far out from the frame (x)... */
export const BIKE_PEDAL_X = 0.13;
/** ...and the right one (+x) this much higher than the bottom bracket, the left one as much lower. */
export const BIKE_CRANK_Y = 0.12;
/** ...and the right one this much ahead of it (z), the left one as much behind: the cranks at rest. */
const BIKE_CRANK_Z = 0.04;
const CRANK_LEN = Math.hypot(BIKE_CRANK_Y, BIKE_CRANK_Z);
const CRANK_REST = Math.atan2(BIKE_CRANK_Z, BIKE_CRANK_Y);

/**
 * Where a pedal is from the bottom bracket (y up, z forward; `out` is
 * written), the right one (`side` 1) or the left (-1), with the cranks
 * turned `turn` radians forward from rest (#138: pedalling turns the top
 * pedal forward).
 */
export function pedalOffset(side: 1 | -1, turn: number, out: THREE.Vector3): THREE.Vector3 {
  const a = CRANK_REST + turn;
  return out.set(side * BIKE_PEDAL_X, side * CRANK_LEN * Math.cos(a), side * CRANK_LEN * Math.sin(a));
}
/** The grips, in the front half's space (from the steering axis), one each side (±x). */
export const BIKE_GRIP = new THREE.Vector3(0.31, 0.98, -0.1);

export interface Bike {
  root: THREE.Group;
  frame: THREE.Group;
  front: THREE.Group;
  /** The two wheels' spinning parts (tyre and spokes), turned about x as the bike rolls. */
  wheels: THREE.Object3D[];
  /**
   * The cranks and pedals, in the frame about the bottom bracket (#138):
   * `rotation.x` is how far they've turned forward from rest (`pedalOffset`),
   * and the pedals turn back by as much so they stay level.
   */
  cranks: THREE.Group;
  pedals: THREE.Object3D[];
}

/** A tube from a to b (in the group's space), `r` thick. */
function tube(a: THREE.Vector3, b: THREE.Vector3, r: number, mat: THREE.Material): THREE.Mesh {
  const len = a.distanceTo(b);
  const geo = new THREE.CylinderGeometry(r, r, len, facets(10));
  const m = inked(geo, mat, 0.008);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  return m;
}

function wheel(tyre: THREE.Material, rim: THREE.Material): THREE.Group {
  const w = new THREE.Group();
  const t = inked(new THREE.TorusGeometry(BIKE_WHEEL_R - 0.03, 0.03, facets(8), facets(24)), tyre, 0.01);
  t.rotation.y = Math.PI / 2;
  w.add(t);
  // Three spokes' worth of chunky mag-wheel arms, so the spin shows.
  for (let i = 0; i < 3; i++) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(0.015, (BIKE_WHEEL_R - 0.05) * 2, 0.03), rim);
    s.rotation.x = (i * Math.PI) / 3;
    w.add(s);
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.1, facets(10)), rim);
  hub.rotation.z = Math.PI / 2;
  w.add(hub);
  return w;
}

function pegs(mat: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const geo = new THREE.CylinderGeometry(0.022, 0.022, 0.1, facets(10));
  geo.rotateZ(Math.PI / 2);
  for (const side of [1, -1]) {
    const p = inked(geo, mat, 0.006);
    p.position.x = side * BIKE_PEG_X;
    g.add(p);
  }
  return g;
}

export function buildBike(look: SkaterLook): Bike {
  const paint = toon(look.deck, { roughness: 0.4 }, "paint");
  const metal = toon("#b9c0cc", { roughness: 0.3, metalness: 0.7 }, "metal");
  const dark = toon("#1f1f26", { roughness: 0.85 });
  const rim = toon(look.wheels, { roughness: 0.4 }, "plastic");

  const root = new THREE.Group();
  root.name = "bmx";
  const frame = new THREE.Group();
  const front = new THREE.Group();
  frame.position.z = BIKE_HEAD_Z;
  front.position.z = BIKE_HEAD_Z;
  root.add(frame, front);

  const R = BIKE_WHEEL_R;
  const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
  // The frame, measured from the steering axis.
  const headTop = v(0, 0.72, -0.04);
  const headBottom = v(0, 0.56, 0);
  const bb = v(0, 0.29, BIKE_PEDAL.z - BIKE_HEAD_Z);
  const seatTop = v(0, 0.66, -0.74);
  const rearHub = v(0, R, BIKE_REAR_HUB);
  frame.add(
    tube(headTop, seatTop, 0.022, paint),
    tube(headBottom, bb, 0.025, paint),
    tube(bb, seatTop, 0.022, paint),
    tube(bb, rearHub, 0.016, paint),
    tube(seatTop, rearHub, 0.014, paint),
    tube(headTop, headBottom, 0.03, paint),
  );
  const seat = inked(new THREE.BoxGeometry(0.1, 0.04, 0.24), dark, 0.008);
  seat.position.set(0, 0.72, -0.8);
  seat.rotation.x = -0.15;
  frame.add(seat, tube(seatTop, v(0, 0.71, -0.78), 0.012, metal));
  // Cranks near upright at rest, the right pedal (+x) forward and up, the left back and down.
  const cranks = new THREE.Group();
  cranks.position.copy(bb);
  frame.add(cranks);
  const pedals: THREE.Object3D[] = [];
  for (const side of [1, -1] as const) {
    const pedalAt = pedalOffset(side, 0, v(0, 0, 0));
    cranks.add(tube(v(side * 0.07, 0, 0), pedalAt.clone().setX(side * 0.07), 0.012, metal));
    const pedal = inked(new THREE.BoxGeometry(0.1, 0.02, 0.08), dark, 0.006);
    pedal.position.copy(pedalAt);
    cranks.add(pedal);
    pedals.push(pedal);
  }
  const sprocket = inked(new THREE.CylinderGeometry(0.07, 0.07, 0.01, facets(16)), metal, 0.006);
  sprocket.rotation.z = Math.PI / 2;
  sprocket.position.copy(bb).add(v(0.05, 0, 0));
  frame.add(sprocket);
  const back = wheel(dark, rim);
  back.position.copy(rearHub);
  const backPegs = pegs(metal);
  backPegs.position.copy(rearHub);
  frame.add(back, backPegs);

  // The front: fork down to the hub on the axis, stem and bars up top.
  const hub = v(0, R, 0);
  for (const side of [1, -1]) front.add(tube(headBottom.clone().add(v(side * 0.05, 0, 0)), hub.clone().add(v(side * 0.05, 0, 0)), 0.014, metal));
  const stem = inked(new THREE.BoxGeometry(0.06, 0.05, 0.08), dark, 0.008);
  stem.position.set(0, 0.78, -0.04);
  front.add(stem);
  const gx = BIKE_GRIP.x;
  front.add(
    tube(v(-gx + 0.04, BIKE_GRIP.y - 0.02, BIKE_GRIP.z), v(gx - 0.04, BIKE_GRIP.y - 0.02, BIKE_GRIP.z), 0.012, metal),
    tube(v(-0.1, 0.78, -0.04), v(-0.14, BIKE_GRIP.y - 0.02, BIKE_GRIP.z), 0.012, metal),
    tube(v(0.1, 0.78, -0.04), v(0.14, BIKE_GRIP.y - 0.02, BIKE_GRIP.z), 0.012, metal),
  );
  for (const side of [1, -1]) {
    const grip = inked(new THREE.CylinderGeometry(0.018, 0.018, 0.1, facets(10)), dark, 0.006);
    grip.rotation.z = Math.PI / 2;
    grip.position.set(side * gx, BIKE_GRIP.y - 0.02, BIKE_GRIP.z);
    front.add(grip);
  }
  const fw = wheel(dark, rim);
  fw.position.copy(hub);
  const frontPegs = pegs(metal);
  frontPegs.position.copy(hub);
  front.add(fw, frontPegs);
  return { root, frame, front, wheels: [back, fw], cranks, pedals };
}
